import { createEntityAdapter, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from 'uuid';
import moment from "moment";
import { ChunkType, IChatMessage, MessageSliceChat, MessageSliceState } from "../../../types";
import { ChatMessage, nebulaChatApi } from "../../services/nebulaChat";

export const messagesAdapter = createEntityAdapter<IChatMessage>({
    selectId: (message) => message.uuid,
    sortComparer: (a, b) => moment(b.time).unix() - moment(a.time).unix()
})

const initialState: MessageSliceState = {
    chat: {}
}

export const messageSlice = createSlice({
    name: 'message',
    initialState,
    reducers: {
        addOneChatToMessage: (state, { payload }: PayloadAction<{ chat_uuid: string, value: MessageSliceChat }>) => ({
            ...state,
            chat: {
                ...state.chat,
                [payload.chat_uuid]: payload.value
            }
        }),
        addManyChatToMessage: (state, { payload }: PayloadAction<Record<string, MessageSliceChat>>) => ({
            ...state,
            chat: {
                ...state.chat,
                ...payload
            }
        }),
        addManyMessage: (state, { payload }: PayloadAction<{
            chat_uuid: string,
            messages: IChatMessage[],
            chunk: ChunkType
        }>) => {
            const activeChat = state.chat[payload.chat_uuid];

            if (!activeChat) return;

            const activeChunk = payload.chunk === 'search' ? activeChat.searchChunk : activeChat.initialChunk

            messagesAdapter.setMany(activeChunk, payload.messages)
        },
        addOneMessage: (state, { payload }: PayloadAction<{ chat_uuid: string; message: IChatMessage }>) => {
            const activeChat = state.chat[payload.chat_uuid];

            if (!activeChat) return;

            messagesAdapter.setOne(activeChat.initialChunk, payload.message)
        },
        mergeChunks: (state, { payload }: PayloadAction<{ chat_uuid: string; messages: IChatMessage[] }>) => {
            const activeChat = state.chat[payload.chat_uuid];

            if (!activeChat) return;

            messagesAdapter.upsertMany(activeChat.initialChunk, payload.messages)
            messagesAdapter.removeAll(activeChat.searchChunk)
        },
        addOneMessageToChunk: (state, { payload }: PayloadAction<{ chat_uuid: string; message: IChatMessage; chunk: ChunkType }>) => {
            const activeChat = state.chat[payload.chat_uuid];

            if (!activeChat) return;

            const activeChunk = payload.chunk === 'search' ? activeChat.searchChunk : activeChat.initialChunk

            if (!activeChunk) return;

            messagesAdapter.setOne(activeChunk, payload.message)
        },
        updateOneMessage: (state, { payload }: PayloadAction<{
            chat_uuid: string,
            message_uuid: string,
            changes: Partial<IChatMessage>
        }>) => {
            const activeChat = state.chat[payload.chat_uuid];

            if (!activeChat) return;

            const activeChunk = activeChat.initialChunk.ids.includes(payload.message_uuid) ? activeChat.initialChunk : activeChat.searchChunk;

            if (!activeChunk) return;

            messagesAdapter.updateOne(activeChunk, {
                id: payload.message_uuid,
                changes: { ...payload.changes }
            })
        },
        updateManyMessages: (state, { payload }: PayloadAction<{
            chat_uuid: string,
            chunk: ChunkType,
            changes: {
                id: string,
                changes: Partial<IChatMessage>
            }[]
        }>) => {

            const activeChat = state.chat[payload.chat_uuid];
            const activeChunk = payload.chunk === 'initial' ? activeChat.initialChunk : activeChat.searchChunk;

            if (!activeChunk) return;

            messagesAdapter.updateMany(activeChunk, payload.changes)
        },
        deleteOneMessage: (state, { payload }: PayloadAction<{ message_uuid: string, chat_uuid: string }>) => {
            const activeChat = state.chat[payload.chat_uuid];

            if (!activeChat) return;

            const activeChunk = activeChat.initialChunk.ids.includes(payload.message_uuid) ? activeChat.initialChunk : activeChat.searchChunk;

            if (!activeChunk) return;

            messagesAdapter.removeOne(activeChunk, payload.message_uuid)
        },
        removeChat: (state, { payload }: PayloadAction<{ conversationUuid: string }>) => {
            // eslint-disable-next-line no-param-reassign
            delete state.chat[payload.conversationUuid];
        },
        pruneConversation: (state, { payload }: PayloadAction<{ chat_uuid: string }>) => {
            const chat = state.chat[payload.chat_uuid];
            if (!chat || chat.initialChunk.ids.length <= 60) return;
            messagesAdapter.removeMany(chat.initialChunk, chat.initialChunk.ids.slice(20) as string[]);
        },
        receivedConversationMessage: (state, { payload: { message } }: PayloadAction<{ message: ChatMessage }>) => {
            const activeChat = state.chat[message.to];

            if (!activeChat) return;

            messagesAdapter.setOne(activeChat.initialChunk, {
                ...message,
                updated_at: "",
                files: message.files?.map((f, index) => ({
                    ...f,
                    content: "",
                    index,
                }) as any)
            } as any)
        },
        startSearch: (state, { payload: { message, conversationUuid } }: PayloadAction<{ message: ChatMessage, conversationUuid: string }>) => {
            const newChat = messagesAdapter.setOne(messagesAdapter.getInitialState(), {
                ...message,
                updated_at: "",
                files: message.files?.map((f, index) => ({
                    ...f,
                    content: "",
                    index,
                }) as any)
            } as any);

            const messageCache = state.chat[conversationUuid];

            if (messageCache) {
                messageCache.searchChunk = newChat;
            }
        }
    },
    extraReducers: builder => {
        builder.addMatcher(nebulaChatApi.endpoints.getLastMessagesN.matchFulfilled, (state, { payload }) => {
            payload.conversations.forEach(c => {
                const existing = state.chat[c.uuid];
                if (existing) {
                    if (c.message && !existing.initialChunk.entities[c.message.uuid]) {
                        messagesAdapter.addOne(existing.initialChunk, {
                            ...c.message,
                            files: c.message?.files?.map((f, index) => ({
                                ...f,
                                content: "",
                                index,
                            })) as any
                        } as any);
                    }
                    return;
                }

                let latestMessage: IChatMessage;
                if (c.message) {
                    latestMessage = {
                        ...c.message,
                        files: c.message?.files?.map((f, index) => ({
                            ...f,
                            content: "",
                            index,
                        })) as any
                    } as any;
                } else {
                    latestMessage = {
                        content: `You've just been added to ${c.display_name}`,
                        time: moment(c.chat_updated_at).format(),
                        updated_at: moment(c.chat_updated_at).format(),
                        from: '',
                        uuid: `${uuidv4()}-noMenu`,
                        to: c.uuid
                    };
                }

                // eslint-disable-next-line no-param-reassign
                state.chat[c.uuid] = {
                    initialChunk: messagesAdapter.addOne(
                        messagesAdapter.getInitialState(),
                        latestMessage
                    ),
                    searchChunk: messagesAdapter.getInitialState(),
                };
            });
        });
        builder.addMatcher(
            nebulaChatApi.endpoints.getMessagesN.matchFulfilled,
            (state, { payload: { messages }, meta: { arg: { originalArgs: { chunk, uuid } } } }) => {
                const activeChat = state.chat[uuid];

                if (!activeChat) {
                    return;
                }
                const newRecords = messages.map(m => ({
                    ...m,
                    files: m.files?.map((f, index) => ({
                        ...f,
                        content: "",
                        index,
                    })) as any
                })) as any[];

                messagesAdapter.setMany(chunk === 'initial' ? activeChat.initialChunk : activeChat.searchChunk, newRecords);
            });
    },
})

export const message = messageSlice.reducer;
